import S, { DataSignal } from 's-js';
import { lift } from 's-array';
import { AssertExtends } from 'lib/assertExtends';
import TZDate from 'lib/TZDate';
import uniqueIndex from 'lib/uniqueIndex';

import { Issue } from 'data/models/Issue';
import { PortCall } from 'data/models/PortCall';

import { PortCallSchedule, PortCallScheduleParams } from 'portcall/portcallschedule/model';

import { AddPortCallTabModel } from './model';
import { RecurringBatch } from 'data/models/RecurringBatch';

export interface AddPortCallSummaryTabParams {
    FinishedOrder : () => FinishedOrderData | null;
}

export interface FinishedOrderData {
    PortCalls : PortCall[];
    Batch : RecurringBatch | null;
}

export type AddPortCallSummaryTab = AssertExtends<ReturnType<typeof AddPortCallSummaryTab>, AddPortCallTabModel>;
export function AddPortCallSummaryTab(
    portcallScheduleModel : (params : PortCallScheduleParams) => PortCallSchedule,
    {
        FinishedOrder
    } : AddPortCallSummaryTabParams
) {
    const
        SelectedPortCallId = S.value<number | null>(null),
        PortCalls = lift(() => FinishedOrder()?.PortCalls ?? [])
            .map(pc => SubmittedPortCall(pc, SelectedPortCallId))
            .orderBy(pc => pc.ArrivalDate() ?? new TZDate(0)),
        portcallsById = uniqueIndex(PortCalls, pc => pc.Model.Id()),
        SelectedPortCall = S(() =>
            SelectedPortCallId() === null ? null : portcallsById(SelectedPortCallId()!)()
        ),
        SelectedSchedule = S(() => SelectedPortCall() && portcallScheduleModel({
            PortCall: SelectedPortCall()!.Model
        })),
        Batch = S(() => (FinishedOrder()?.Batch ?? null) && SubmittedBatch(FinishedOrder()!.Batch!));

    // Auto-select the first PortCall
    S(() => PortCalls().length > 0 && SelectedPortCallId() === null && SelectedPortCallId(PortCalls()[0].Model.Id()));

    return {
        PortCalls,
        SelectedSchedule,
        Batch,
        CanProceed: () => true
    };
}

export type SubmittedPortCall = ReturnType<typeof SubmittedPortCall>;
function SubmittedPortCall(
    PortCall : PortCall,
    SelectedPortCallId : DataSignal<number | null>
) {
    const
        ArrivalDate = S(() => PortCall.Itinerary().find(pt => pt.IsPortArrival())?.Arrival.Estimated() ?? null),
        Issues = S(() =>
            PortCall
                .Reservations()
                .reduce((acc, br) => acc.concat(br.Issues()), [] as Issue[])
                // TODO: Include PJO issues here? It seems like it only makes sense to include them here if
                // we're gonna have a place to display them on the PortCallScheduleView (or WHATEVER summary UI
                // we choose to use for individual PortCalls).
                .sort(Issue.ReversePrecedenceOrder)
        ),
        // A separate S.value to avoid notifying downstream code about selectedPortCallId changes unless THIS
        // PortCall's selection state is actually changing.
        IsSelected = S.value(S.sample(() => SelectedPortCallId() === PortCall.Id()));

    S(() => IsSelected(SelectedPortCallId() === PortCall.Id()));

    return {
        Model: PortCall,
        ArrivalDate,
        Issues,
        IsSelected,
        Select
    };

    function Select() {
        SelectedPortCallId(PortCall.Id());
    }
}

export type SubmittedBatch = ReturnType<typeof SubmittedBatch>;
function SubmittedBatch(batch : RecurringBatch) {
    return { Model: batch };
}



// WEBPACK FOOTER //
// ./apps/portcall/addportcall/summary.ts